import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthStateService } from '../../core/services/auth-state.service';
import { PtReservationService } from '../../core/services/pt-reservation.service';
import { businessDayjs } from '../../core/time/business-calendar';

const DAY = 86400000;
function epoch(value: string): number {
  if (!/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return NaN;
  const ms = Date.parse(value + 'T00:00:00Z');
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value ? ms : NaN;
}

@Component({
  selector: 'app-reservation-csv',
  imports: [FormsModule],
  template: `
    @if (allowed) {
      <button class="form-group btn btn-success" type="button" (click)="open()"><i class="fa s-icon-dawnload"></i>&nbsp;予約履歴CSVダウンロード</button>
    }
    @if (opened && allowed) {
      <div class="modal-backdrop fade show"></div>
      <div class="modal fade show d-block" id="reservationCsvModal" tabindex="-1" role="dialog"
        aria-labelledby="reservationCsvModalLabel" aria-modal="true" data-keyboard="false" data-backdrop="static" [attr.aria-busy]="downloading">
        <div class="modal-dialog"><div class="modal-content">
          <div class="modal-header"><h5 class="modal-title" id="reservationCsvModalLabel">予約履歴CSVダウンロード</h5>
            <button class="close" type="button" aria-label="閉じる" [disabled]="downloading" (click)="close()"><span aria-hidden="true">&times;</span></button>
          </div>
          <form #csvForm="ngForm" novalidate (ngSubmit)="download()">
            <div class="modal-body">
              @if (message) { <div class="alert" role="status" [class.alert-success]="messageType === 'success'" [class.alert-info]="messageType === 'info'" [class.alert-danger]="messageType === 'danger'">{{message}}</div> }
              <p class="mb-2"><strong>対象：全店舗</strong></p><p class="text-muted">実施日を指定してください。</p>
              <div class="form-group"><label for="reservationCsvFrom">開始日</label>
                <input id="reservationCsvFrom" class="form-control" name="from" type="date" min="1000-01-01" max="9999-12-31" required [(ngModel)]="from" (ngModelChange)="adjust('from')" [disabled]="downloading">
              </div>
              <div class="form-group"><label for="reservationCsvTo">終了日</label>
                <input id="reservationCsvTo" class="form-control" name="to" type="date" min="1000-01-01" max="9999-12-31" required [(ngModel)]="to" (ngModelChange)="adjust('to')" [disabled]="downloading">
              </div>
              <p class="small text-muted">キャンセル済み予約を含みます。<br>開始日・終了日を含めて最大31日分を出力可能です。</p>
              @if (rangeError) { <div class="text-danger" role="alert">{{rangeError}}</div> }
            </div>
            <div class="modal-footer">
              <button class="btn bg-close text-white" type="button" [disabled]="downloading" (click)="close()"><i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる</button>
              <button class="btn btn-success" type="submit" [disabled]="downloading || csvForm.invalid || !!rangeError"><i class="fa" [class.fa-spinner]="downloading" [class.fa-spin]="downloading" [class.s-icon-dawnload]="!downloading"></i>&nbsp;{{downloading ? 'ダウンロード中…' : 'ダウンロード'}}</button>
            </div>
          </form>
        </div></div>
      </div>
    }
  `,
})
export class ReservationCsvComponent {
  private readonly auth = inject(AuthStateService);
  private readonly reservations = inject(PtReservationService);
  opened = false;
  downloading = false;
  from = '';
  to = '';
  message = '';
  messageType = '';

  get allowed(): boolean {
    const role = Number(this.auth.currentUser?.role);
    return Number.isInteger(role) && role > 0 && role <= 10;
  }
  open(): void {
    if (!this.allowed || this.downloading) return;
    const today = businessDayjs();
    this.from = today.subtract(30, 'day').format('YYYY-MM-DD');
    this.to = today.format('YYYY-MM-DD');
    this.message = ''; this.messageType = ''; this.opened = true;
  }
  close(): void { if (!this.downloading) this.opened = false; }
  get rangeError(): string {
    if (!this.from || !this.to) return '開始日と終了日を入力してください。';
    const begin = epoch(this.from), end = epoch(this.to);
    if (!Number.isFinite(begin) || !Number.isFinite(end)) return '開始日と終了日を正しい日付で入力してください。';
    const days = (end - begin) / DAY + 1;
    if (days < 1) return '終了日は開始日以降を指定してください。';
    if (days > 31) return '開始日から終了日までを、両日を含めて31日以内で指定してください。';
    return '';
  }
  adjust(changed: 'from' | 'to'): void {
    this.message = '';
    const begin = epoch(this.from), end = epoch(this.to);
    if (!Number.isFinite(begin) || !Number.isFinite(end) || end - begin < 31 * DAY) return;
    if (changed === 'from') this.to = new Date(begin + 30 * DAY).toISOString().slice(0, 10);
    else this.from = new Date(end - 30 * DAY).toISOString().slice(0, 10);
  }
  async download(): Promise<void> {
    if (this.downloading) return;
    this.message = ''; this.messageType = 'danger';
    if (!this.allowed) { this.message = '許可されていない操作です。'; return; }
    if (this.rangeError) { this.message = this.rangeError; return; }
    const from = this.from, to = this.to;
    this.downloading = true;
    try {
      const response = await this.reservations.downloadCsv(from, to);
      if (response.status === 204) { this.messageType = 'info'; this.message = '対象データがありません。'; return; }
      if (!response.headers.get('Content-Type')?.includes('text/csv')) throw new Error('CSVのダウンロードに失敗しました。再度お試しください。');
      const blob = new Blob(['\uFEFF' + (response.body ?? '').replace(/^\uFEFF/, '')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob), link = document.createElement('a');
      link.href = url; link.download = `予約履歴_${from}_${to}.csv`; document.body.append(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.messageType = 'success'; this.message = 'CSVをダウンロードしました。';
    } catch (error: any) {
      let detail: string | undefined;
      try { detail = (typeof error.error === 'string' ? JSON.parse(error.error) : error.error)?.error; } catch { /* Non-JSON transport error. */ }
      this.message = error.status === 401 ? 'ログインの有効期限が切れました。再度ログインしてください。'
        : detail || (error instanceof Error && !('status' in error) ? error.message : '') || 'CSVのダウンロードに失敗しました。再度お試しください。';
    } finally { this.downloading = false; }
  }
}
